// List / manage a private parking space. Short mobile-first form; the listing is
// placed at the position passed in (map centre / current location).

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Camera, Trash2, Pencil, Plus, ChevronLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import {
  createListing,
  fetchMyListings,
  updateListing,
  deleteListing,
  resizePhoto,
  listingPhotoUrl,
  PERIOD_LABEL,
} from "../lib/privateParking";
import { useBackdropDismiss } from "../lib/useBackdropDismiss";

const PERIODS = [
  { key: "hourly", label: "Hourly" },
  { key: "daily", label: "Daily" },
  { key: "monthly", label: "Monthly" },
];

const EMPTY = { title: "", price: "", period: "monthly", description: "", whatsapp: "", email: "" };

export default function PrivateParkingSheet({ open, onClose, pos, onChanged }) {
  const dismissProps = useBackdropDismiss(open, onClose);
  const [mine, setMine] = React.useState(null);
  const [maxListings, setMaxListings] = React.useState(3);
  const [mode, setMode] = React.useState("list"); // list | create | edit
  const [form, setForm] = React.useState(EMPTY);
  const [editing, setEditing] = React.useState(null);
  const [photo, setPhoto] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const data = await fetchMyListings();
      setMine(data.listings || []);
      setMaxListings(data.max_listings || 3);
    } catch {
      setMine([]);
    }
  }, []);

  React.useEffect(() => {
    if (!open) return;
    setMode("list");
    setForm(EMPTY);
    setPhoto(null);
    setPreview(null);
    load();
  }, [open, load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("That photo is too large.");
      return;
    }
    const resized = await resizePhoto(file);
    setPhoto(resized);
    setPreview(URL.createObjectURL(resized));
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!pos) {
      toast.error("We need a map position — allow location and try again.");
      return;
    }
    if (form.title.trim().length < 3) return toast.error("Add a short title.");
    if (!(Number(form.price) > 0)) return toast.error("Add a price.");
    if (!photo) return toast.error("Add one photo of the space.");
    if (!form.whatsapp.trim() && !form.email.trim())
      return toast.error("Add a WhatsApp number or an email so renters can reach you.");

    setBusy(true);
    try {
      await createListing({ ...form, price: Number(form.price), lat: pos.lat, lng: pos.lng, photo });
      toast.success("Your space is listed.");
      setForm(EMPTY);
      setPhoto(null);
      setPreview(null);
      setMode("list");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create the listing.");
    } finally {
      setBusy(false);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateListing(editing.id, {
        title: form.title,
        price: Number(form.price),
        period: form.period,
        description: form.description,
        whatsapp: form.whatsapp || null,
        email: form.email || null,
      });
      toast.success("Listing updated.");
      setMode("list");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update the listing.");
    } finally {
      setBusy(false);
    }
  };

  const toggleAvailable = async (l) => {
    setBusy(true);
    try {
      await updateListing(l.id, { available: !l.available });
      await load();
      onChanged?.();
    } catch {
      toast.error("Could not update availability.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (l) => {
    setBusy(true);
    try {
      await deleteListing(l.id);
      toast.success("Listing deleted.");
      await load();
      onChanged?.();
    } catch {
      toast.error("Could not delete the listing.");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (l) => {
    setEditing(l);
    setForm({
      title: l.title || "",
      price: String(l.price ?? ""),
      period: l.period || "monthly",
      description: l.description || "",
      whatsapp: l.contact?.whatsapp || "",
      email: l.contact?.email || "",
    });
    setMode("edit");
  };

  const fields = (
    <>
      <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Title</label>
      <input
        value={form.title}
        onChange={set("title")}
        maxLength={60}
        placeholder="e.g. Garage in Sliema"
        className="mt-1 mb-3 w-full h-11 px-3 rounded-xl bg-[#0a0a0c] border border-white/10 text-white text-sm outline-none focus:border-[#ccff00]/60"
        data-testid="pp-title"
      />
      <div className="flex gap-2">
        <div className="w-28">
          <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Price €</label>
          <input
            value={form.price}
            onChange={set("price")}
            inputMode="decimal"
            placeholder="120"
            className="mt-1 w-full h-11 px-3 rounded-xl bg-[#0a0a0c] border border-white/10 text-white text-sm outline-none focus:border-[#ccff00]/60"
            data-testid="pp-price"
          />
        </div>
        <div className="flex-1">
          <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Period</label>
          <div className="mt-1 flex gap-1">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setForm((f) => ({ ...f, period: p.key }))}
                className={`flex-1 h-11 rounded-xl text-[11px] font-medium border transition-colors ${
                  form.period === p.key
                    ? "bg-[#ccff00] text-[#0a0a0c] border-[#ccff00]"
                    : "bg-[#0a0a0c] text-zinc-300 border-white/10"
                }`}
                data-testid={`pp-period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <label className="mt-3 block text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
        Short description
      </label>
      <textarea
        value={form.description}
        onChange={set("description")}
        maxLength={300}
        rows={2}
        placeholder="Secure underground garage, 2 min from the seafront."
        className="mt-1 w-full px-3 py-2 rounded-xl bg-[#0a0a0c] border border-white/10 text-white text-sm outline-none focus:border-[#ccff00]/60 resize-none"
        data-testid="pp-description"
      />

      <div className="mt-3 flex gap-2">
        <div className="flex-1">
          <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">WhatsApp</label>
          <input
            value={form.whatsapp}
            onChange={set("whatsapp")}
            inputMode="tel"
            placeholder="+356 7712 3456"
            className="mt-1 w-full h-11 px-3 rounded-xl bg-[#0a0a0c] border border-white/10 text-white text-sm outline-none focus:border-[#ccff00]/60"
            data-testid="pp-whatsapp"
          />
        </div>
        <div className="flex-1">
          <label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Email</label>
          <input
            value={form.email}
            onChange={set("email")}
            inputMode="email"
            placeholder="you@email.com"
            className="mt-1 w-full h-11 px-3 rounded-xl bg-[#0a0a0c] border border-white/10 text-white text-sm outline-none focus:border-[#ccff00]/60"
            data-testid="pp-email"
          />
        </div>
      </div>
      <p className="mt-1.5 text-[10px] text-zinc-500">
        Shown only when someone taps “Contact owner”. WhatsApp is used first if both are filled.
      </p>
    </>
  );

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[3400] bg-black/75"
            {...dismissProps}
            data-testid="pp-backdrop"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 280 }}
            className="fixed inset-x-0 bottom-0 z-[3401] bg-[#141416] border-t border-white/10 rounded-t-3xl p-5 pb-8 max-h-[92vh] overflow-y-auto sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:bottom-6 sm:rounded-3xl sm:border sm:w-[440px] sm:max-w-[92vw]"
            data-testid="pp-sheet"
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
                  Private parking
                </div>
                <div className="font-heading text-xl font-semibold text-white mt-1 leading-tight">
                  {mode === "list" ? "Your spaces" : mode === "edit" ? "Edit listing" : "List your space"}
                </div>
                <p className="text-[12px] text-zinc-400 mt-1 leading-snug">
                  Rent out a garage or driveway you're not using.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95 transition-transform"
                data-testid="pp-close"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>

            {mode === "list" && (
              <div data-testid="pp-list-view">
                {mine === null && (
                  <div className="py-6 flex justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-[#ccff00]" />
                  </div>
                )}
                {mine?.length === 0 && (
                  <p className="text-[13px] text-zinc-400 leading-snug" data-testid="pp-empty">
                    You have no listings yet. Add a photo, a price and how to reach you — takes a minute.
                  </p>
                )}
                {mine?.map((l) => (
                  <div
                    key={l.id}
                    className="mt-2 p-3 rounded-2xl bg-[#0a0a0c] border border-white/10"
                    data-testid={`pp-item-${l.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {l.has_photo && (
                        <img
                          src={listingPhotoUrl(l.id)}
                          alt={l.title}
                          className="w-12 h-12 rounded-xl object-cover shrink-0"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold text-white truncate">{l.title}</div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">
                          €{l.price} {PERIOD_LABEL[l.period]} · {l.available ? "Available" : "Unavailable"}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2.5 flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => startEdit(l)}
                        disabled={busy}
                        className="flex-1 h-10 rounded-full bg-white/[0.04] border border-white/10 text-[11px] text-white inline-flex items-center justify-center gap-1.5"
                        data-testid={`pp-edit-${l.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleAvailable(l)}
                        disabled={busy}
                        className="flex-1 h-10 rounded-full bg-white/[0.04] border border-white/10 text-[11px] text-white inline-flex items-center justify-center gap-1.5"
                        data-testid={`pp-toggle-${l.id}`}
                      >
                        {l.available ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        {l.available ? "Mark unavailable" : "Mark available"}
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(l)}
                        disabled={busy}
                        className="w-10 h-10 rounded-full bg-red-500/10 border border-red-500/30 text-red-200 inline-flex items-center justify-center"
                        data-testid={`pp-delete-${l.id}`}
                        aria-label="Delete listing"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}

                {mine !== null && mine.length < maxListings && (
                  <button
                    type="button"
                    onClick={() => setMode("create")}
                    className="mt-4 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                    data-testid="pp-new-btn"
                  >
                    <Plus className="w-4 h-4" />
                    List a space
                  </button>
                )}
              </div>
            )}

            {mode === "create" && (
              <form onSubmit={submitCreate} data-testid="pp-create-form">
                <button
                  type="button"
                  onClick={() => setMode("list")}
                  className="inline-flex items-center gap-1.5 text-[12px] text-zinc-400 mb-3"
                  data-testid="pp-back-btn"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Your spaces
                </button>

                <label
                  className="flex items-center gap-3 p-3 rounded-2xl bg-[#0a0a0c] border border-dashed border-white/15 cursor-pointer"
                  data-testid="pp-photo-label"
                >
                  {preview ? (
                    <img src={preview} alt="Preview" className="w-14 h-14 rounded-xl object-cover" />
                  ) : (
                    <span className="w-14 h-14 rounded-xl bg-white/5 flex items-center justify-center">
                      <Camera className="w-5 h-5 text-[#ccff00]" />
                    </span>
                  )}
                  <span className="text-[12px] text-zinc-300">
                    {photo ? "Photo added — tap to change" : "Add 1 photo of the space"}
                  </span>
                  <input type="file" accept="image/*" onChange={pickPhoto} className="hidden" data-testid="pp-photo-input" />
                </label>

                <div className="mt-3">{fields}</div>

                <p className="mt-3 text-[10px] text-zinc-500">
                  The listing is placed at your current location
                  {pos ? ` (${pos.lat.toFixed(4)}, ${pos.lng.toFixed(4)})` : ""}.
                </p>

                <button
                  type="submit"
                  disabled={busy}
                  className="mt-3 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
                  data-testid="pp-submit"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Publish listing
                </button>
              </form>
            )}

            {mode === "edit" && (
              <form onSubmit={submitEdit} data-testid="pp-edit-form">
                <button
                  type="button"
                  onClick={() => setMode("list")}
                  className="inline-flex items-center gap-1.5 text-[12px] text-zinc-400 mb-3"
                  data-testid="pp-back-btn-edit"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Your spaces
                </button>
                {fields}
                <button
                  type="submit"
                  disabled={busy}
                  className="mt-3 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
                  data-testid="pp-save"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Save changes
                </button>
              </form>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
